import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, User, School, MapPin, Phone, HeartPulse, Brain, ArrowLeft, Wallet, Users } from "lucide-react";
import { toast } from "sonner";
import { COMPETITIONS, WORKSHOPS, API_BASE } from "@/lib/festival";
import SuccessScreen from "@/components/SuccessScreen";

const initial = {
  full_name: "",
  age: "",
  school_name: "",
  area: "",
  companion_name: "",
  companion_phone: "",
  health_issue: null,
  health_issue_details: "",
  psych_issue: null,
  psych_issue_details: "",
  competitions: [],
  workshops: [],
};

export default function RegistrationForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [preview, setPreview] = useState({ existing_count: 0, next_fee: 50, bulk_active: false });

  // Live fee preview when phone changes (debounced)
  useEffect(() => {
    const phone = form.companion_phone.trim();
    if (phone.replace(/\D/g, "").length < 7) {
      setPreview({ existing_count: 0, next_fee: 50, bulk_active: false });
      return;
    }
    const t = setTimeout(() => {
      axios
        .get(`${API_BASE}/register/preview`, { params: { phone } })
        .then((r) => setPreview(r.data))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.companion_phone]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, id) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
    }));

  const validate = () => {
    if (!form.full_name.trim() || form.full_name.trim().split(/\s+/).length < 2)
      return "الاسم الثلاثي مطلوب (ثلاث كلمات على الأقل)";
    const age = Number(form.age);
    if (!age || age < 4 || age > 16) return "العمر يجب أن يكون بين 4 و 16 سنة";
    if (!form.school_name.trim()) return "اسم المدرسة مطلوب";
    if (!form.area.trim()) return "منطقة السكن مطلوبة";
    if (!form.companion_name.trim()) return "اسم المرافق مطلوب";
    if (!/^[0-9+\s-]{7,15}$/.test(form.companion_phone.trim())) return "رقم هاتف المرافق غير صحيح";
    if (form.health_issue === null) return "يرجى الإجابة على سؤال الحالة الصحية";
    if (form.psych_issue === null) return "يرجى الإجابة على السؤال النفسي";
    if (form.competitions.length === 0 && form.workshops.length === 0)
      return "اختر مسابقة أو ورشة واحدة على الأقل";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, age: Number(form.age), health_issue: !!form.health_issue, psych_issue: !!form.psych_issue };
      // Convert competition/workshop ids to labels for readability
      payload.competitions = form.competitions.map((id) => COMPETITIONS.find((c) => c.id === id)?.label || id);
      payload.workshops = form.workshops.map((id) => WORKSHOPS.find((w) => w.id === id)?.label || id);
      const res = await axios.post(`${API_BASE}/register`, payload);
      setSuccess(res.data);
      setForm(initial);
      setPreview({ existing_count: 0, next_fee: 50, bulk_active: false });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "حدث خطأ أثناء التسجيل");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="register" className="relative py-24 sm:py-32" data-testid="register-section">
      <div className="container-x">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD600] text-[#1E293B] text-xs font-black mb-3">
            التسجيل الآن مفتوح
          </div>
          <h2 className="font-display text-3xl sm:text-5xl font-black text-[#1E293B]" data-testid="form-title">
            استمارة تسجيل الطفل
          </h2>
          <p className="mt-3 text-[#475569] font-semibold">
            املأ البيانات بعناية — كل المعلومات محفوظة لدى إدارة المهرجان لضمان تجربة آمنة وممتعة.
          </p>
        </div>

        <form onSubmit={submit} className="playful-card !p-6 sm:!p-10 max-w-4xl" data-testid="registration-form">
          {/* Fee banner */}
          <div className="mb-6 rounded-2xl overflow-hidden border-2 border-[#FFD600] bg-gradient-to-l from-[#FFD600]/25 via-[#FFFDF0] to-[#0066CC]/10" data-testid="fee-banner">
            <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-[#0066CC] text-white flex items-center justify-center flex-shrink-0">
                <Wallet className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 text-right min-w-0">
                <div className="font-display font-black text-[#1E293B] text-sm sm:text-lg leading-tight">
                  رسوم التسجيل: <span className="text-[#0066CC] whitespace-nowrap">50 د.ل</span> للطفل الواحد
                </div>
                <div className="text-xs sm:text-sm text-[#475569] font-semibold mt-1.5 flex items-start gap-1.5 leading-relaxed">
                  <Users className="w-3.5 h-3.5 text-[#E63946] flex-shrink-0 mt-0.5" />
                  <span>
                    عرض العائلات: 3 أطفال أو أكثر ←{" "}
                    <span className="text-[#E63946] font-black whitespace-nowrap">35 د.ل</span>{" "}
                    لكل طفل
                  </span>
                </div>
                <div className="text-[11px] sm:text-xs text-[#0066CC] font-bold mt-2 leading-relaxed">
                  ℹ️ يتم ضبط السعر <u>تلقائيًا</u> بمجرد تسجيل الطفل الثالث بنفس رقم هاتف المرافق — وستُخفَّض رسوم إخوته السابقين أيضًا إلى <span className="whitespace-nowrap">35 د.ل</span>.
                </div>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field icon={User} label="اسم الطفل الثلاثي" testid="full-name">
              <input
                type="text"
                className="input"
                placeholder="مثال: أحمد محمد الصادق"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                data-testid="input-full-name"
              />
            </Field>
            <Field icon={User} label="عمر الطفل" testid="age">
              <input
                type="number"
                min="4"
                max="16"
                className="input"
                placeholder="بالسنوات"
                value={form.age}
                onChange={(e) => update("age", e.target.value)}
                data-testid="input-age"
              />
            </Field>
            <Field icon={School} label="اسم المدرسة" testid="school">
              <input
                type="text"
                className="input"
                placeholder="مدرسة النور الابتدائية"
                value={form.school_name}
                onChange={(e) => update("school_name", e.target.value)}
                data-testid="input-school"
              />
            </Field>
            <Field icon={MapPin} label="منطقة السكن" testid="area">
              <input
                type="text"
                className="input"
                placeholder="طرابلس - حي الأندلس"
                value={form.area}
                onChange={(e) => update("area", e.target.value)}
                data-testid="input-area"
              />
            </Field>
            <Field icon={User} label="اسم المرافق" testid="companion-name">
              <input
                type="text"
                className="input"
                placeholder="الأب / الأم / ولي الأمر"
                value={form.companion_name}
                onChange={(e) => update("companion_name", e.target.value)}
                data-testid="input-companion-name"
              />
            </Field>
            <Field icon={Phone} label="رقم هاتف المرافق" testid="companion-phone">
              <input
                type="tel"
                dir="ltr"
                className="input text-left"
                placeholder="+218 91 234 5678"
                value={form.companion_phone}
                onChange={(e) => update("companion_phone", e.target.value)}
                data-testid="input-companion-phone"
              />
            </Field>
          </div>

          {/* Health */}
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <YesNo
              icon={HeartPulse}
              label="هل يعاني الطفل من أي مرض صحي؟"
              value={form.health_issue}
              onChange={(v) => update("health_issue", v)}
              testid="health"
            />
            <YesNo
              icon={Brain}
              label="هل يوجد أي أمر نفسي يحتاج رعاية خاصة؟"
              value={form.psych_issue}
              onChange={(v) => update("psych_issue", v)}
              testid="psych"
            />
          </div>

          <AnimatePresence>
            {form.health_issue === true && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4">
                  <textarea
                    rows={2}
                    className="input"
                    placeholder="يرجى ذكر الحالة الصحية والأدوية إن وجدت"
                    value={form.health_issue_details}
                    onChange={(e) => update("health_issue_details", e.target.value)}
                    data-testid="input-health-details"
                  />
                </div>
              </motion.div>
            )}
            {form.psych_issue === true && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4">
                  <textarea
                    rows={2}
                    className="input"
                    placeholder="يرجى وصف الحاجة بشكل مختصر"
                    value={form.psych_issue_details}
                    onChange={(e) => update("psych_issue_details", e.target.value)}
                    data-testid="input-psych-details"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chips - Competitions */}
          <ChipSection
            title="اختر المسابقات (يمكن اختيار أكثر من واحدة)"
            items={COMPETITIONS}
            selected={form.competitions}
            onToggle={(id) => toggleArr("competitions", id)}
            activeClass="active-blue"
            testid="competitions"
          />

          {/* Chips - Workshops */}
          <ChipSection
            title="اختر ورش العمل"
            items={WORKSHOPS}
            selected={form.workshops}
            onToggle={(id) => toggleArr("workshops", id)}
            activeClass="active-red"
            testid="workshops"
          />

          <div className="mt-8 sm:mt-10 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-black text-xs sm:text-sm ${
                  preview.bulk_active ? "bg-[#E63946] text-white" : "bg-[#0066CC] text-white"
                }`}
                data-testid="fee-preview"
              >
                <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                رسوم هذا الطفل: {preview.next_fee} د.ل
              </div>
              {preview.existing_count > 0 && (
                <span className="text-[11px] sm:text-xs text-[#475569] font-bold">
                  ({preview.existing_count} طفل مسجّل — رقم {preview.existing_count + 1})
                </span>
              )}
              {preview.bulk_active && (
                <span className="text-[11px] sm:text-xs text-[#10B981] font-black">✓ سعر العائلات مُفعّل</span>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-3d w-full sm:w-auto sm:self-end justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جارٍ الإرسال...
                </>
              ) : (
                <>
                  إرسال التسجيل
                  <ArrowLeft className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 0.85rem 1rem;
          border: 1.5px solid #E2E8F0;
          border-radius: 1rem;
          background: #FFFDF0;
          font-family: inherit;
          font-weight: 600;
          color: #1E293B;
          transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease;
        }
        .input:focus { outline: none; border-color: #0066CC; background: #fff; box-shadow: 0 0 0 4px rgba(0,102,204,.12); }
      `}</style>

      <AnimatePresence>
        {success && <SuccessScreen data={success} onClose={() => setSuccess(null)} />}
      </AnimatePresence>
    </section>
  );
}

function Field({ icon: Icon, label, children, testid }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <span className="text-sm font-black text-[#1E293B] mb-2 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#0066CC]" />
        {label}
      </span>
      {children}
    </label>
  );
}

function YesNo({ icon: Icon, label, value, onChange, testid }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-[#FFFDF0] p-4" data-testid={`yesno-${testid}`}>
      <div className="text-sm font-black text-[#1E293B] mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#E63946]" />
        {label}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`flex-1 py-2.5 rounded-xl font-black text-sm transition-colors ${value === true ? "bg-[#E63946] text-white" : "bg-white border border-[#E2E8F0] text-[#1E293B]"}`}
          data-testid={`yesno-${testid}-yes`}
        >
          نعم
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`flex-1 py-2.5 rounded-xl font-black text-sm transition-colors ${value === false ? "bg-[#10B981] text-white" : "bg-white border border-[#E2E8F0] text-[#1E293B]"}`}
          data-testid={`yesno-${testid}-no`}
        >
          لا
        </button>
      </div>
    </div>
  );
}

function ChipSection({ title, items, selected, onToggle, activeClass, testid }) {
  const allSelected = items.length > 0 && items.every((i) => selected.includes(i.id));
  return (
    <div className="mt-8" data-testid={`chips-${testid}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-lg font-black text-[#1E293B]">{title}</h3>
        <button
          type="button"
          onClick={() => {
            if (allSelected) items.forEach((i) => selected.includes(i.id) && onToggle(i.id));
            else items.forEach((i) => !selected.includes(i.id) && onToggle(i.id));
          }}
          className="text-xs font-black text-[#0066CC] hover:underline"
          data-testid={`chips-${testid}-select-all`}
        >
          {allSelected ? "إلغاء الكل" : "اختيار الكل"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {items.map((it) => {
          const active = selected.includes(it.id);
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onToggle(it.id)}
              className={`chip ${active ? activeClass : ""}`}
              data-testid={`chip-${testid}-${it.id}`}
            >
              {active && <Check className="w-3.5 h-3.5" />}
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
